import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserAuth } from '../contexts/UserAuthContext';

function Header({ activeInfoTab, onInfoTabClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useUserAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const getInitials = () => {
    if (!user?.nombre) return user?.email?.[0]?.toUpperCase() || '?';
    return user.nombre.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isDashboard = location.pathname === '/';

  const buttons = [
    { id: '/', emoji: '🏠', label: 'Inicio' },
    { id: '/nuevo-pedido', emoji: '✍️', label: 'Nuevo Pedido' },
    { id: '/contenido', emoji: '📖', label: 'Contenido' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (isDashboard) {
        const distanceFromBottom =
          document.documentElement.scrollHeight -
          window.innerHeight -
          window.scrollY;
        setIsScrolled(distanceFromBottom > 80);
      } else {
        setIsScrolled(window.scrollY > 80);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDashboard]);

  const handleInfoTabClick = (tab) => {
    if (onInfoTabClick) {
      onInfoTabClick(activeInfoTab === tab ? null : tab);
    }
  };

  const userAuthElement = (
    <div className="header-auth-floating" ref={menuRef}>
      {isAuthenticated && user ? (
        <>
          <button
            className="header-auth-floating-trigger"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="header-auth-avatar" />
            ) : (
              <span className="header-auth-avatar-placeholder">{getInitials()}</span>
            )}
          </button>
          {showUserMenu && (
            <div className="header-user-menu-dropdown">
              <div className="header-user-menu-email">{user.email}</div>
              <button
                className="header-user-menu-item"
                onClick={() => { setShowUserMenu(false); navigate('/perfil'); }}
              >
                Mi perfil
              </button>
              <button className="header-user-menu-item logout" onClick={handleLogout}>
                Cerrar sesión
              </button>
            </div>
          )}
        </>
      ) : (
        <button className="header-auth-floating-btn" onClick={() => navigate('/login')} aria-label="Iniciar sesión">
          <svg className="header-auth-floating-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          <span className="header-auth-floating-text">Iniciar sesión</span>
        </button>
      )}
    </div>
  );

  return (
    <header className={`header-new header-sticky ${isScrolled ? 'header-scrolled' : ''}`}>
      {userAuthElement}

      <div className="header-new-container">
        <div className="header-new-title">
          <h1>Pedidos de Oración</h1>
          <p>Uniendo corazones en oración desde todos los rincones del mundo</p>
        </div>

        <nav className="header-new-nav">
          {buttons.map((button) => {
            const isActive = location.pathname === button.id;
            
            return (
              <Link
                key={button.id}
                to={button.id}
                className={`header-new-button ${isActive ? 'active' : ''}`}
              >
                <span className="header-new-button-emoji">
                  {button.emoji}
                </span>
                <span className="header-new-button-label">
                  {button.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="header-compact">
        <div className="header-compact-title">
          <h2>Pedidos de Oración</h2>
        </div>
        <nav className="header-compact-nav">
          {buttons.map((button) => {
            const isActive = location.pathname === button.id;
            
            return (
              <Link
                key={button.id}
                to={button.id}
                className={`header-compact-button ${isActive ? 'active' : ''}`}
                title={button.label}
              >
                <span className="header-compact-icon">{button.emoji}</span>
              </Link>
            );
          })}
          {isDashboard && (
            <>
              <span className="header-compact-separator" />
              <button
                className={`header-compact-button header-compact-info-btn ${activeInfoTab === 'events' ? 'active' : ''}`}
                onClick={() => handleInfoTabClick('events')}
                title="Eventos"
              >
                <span className="header-compact-icon">📅</span>
              </button>
              <button
                className={`header-compact-button header-compact-info-btn ${activeInfoTab === 'stats' ? 'active' : ''}`}
                onClick={() => handleInfoTabClick('stats')}
                title="Pedidos"
              >
                <span className="header-compact-icon">📊</span>
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Header;
